import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, HelpCircle, Send, CheckCircle, Phone, MapPin } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function Contact() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const subjects = [
    { value: 'general', label: language === 'es' ? 'Pregunta General' : 'General Question' },
    { value: 'support', label: language === 'es' ? 'Soporte Técnico' : 'Technical Support' },
    { value: 'transaction', label: language === 'es' ? 'Problema de Transacción' : 'Transaction Issue' },
    { value: 'merchant', label: language === 'es' ? 'Consulta de Comerciante' : 'Merchant Inquiry' },
    { value: 'partnership', label: language === 'es' ? 'Asociación de Negocios' : 'Business Partnership' },
    { value: 'press', label: language === 'es' ? 'Consulta de Prensa' : 'Press Inquiry' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(language === 'es' ? 'Mensaje enviado exitosamente' : 'Message sent successfully');
      } else {
        // Fallback - show email option
        toast.info(
          language === 'es' 
            ? 'Por favor envíe un correo a support@pecashglobal.com'
            : 'Please email us at support@pecashglobal.com'
        );
      }
    } catch (error) {
      // Fallback - show email option
      toast.info(
        language === 'es' 
          ? 'Por favor envíe un correo a support@pecashglobal.com'
          : 'Please email us at support@pecashglobal.com'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
          <motion.div {...fadeInUp} className="text-center max-w-md">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">
              {language === 'es' ? '¡Mensaje Enviado!' : 'Message Sent!'}
            </h1>
            <p className="text-slate-600 mb-8">
              {language === 'es' 
                ? 'Gracias por contactarnos. Responderemos a su mensaje lo antes posible.'
                : 'Thank you for contacting us. We\'ll respond to your message as soon as possible.'}
            </p>
            <Button 
              onClick={() => setIsSubmitted(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {language === 'es' ? 'Enviar Otro Mensaje' : 'Send Another Message'}
            </Button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={language === 'es' ? 'Contáctenos' : 'Contact Us'}
        description={language === 'es' 
          ? '¿Tiene preguntas? Contáctenos para soporte, consultas de negocios, o información.'
          : 'Have questions? Contact PeCash for support, business inquiries, or information.'}
        canonical="https://pecashglobal.com/contact"
        lang={language}
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-6">
                <MessageCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Contáctenos' : 'Contact Us'}
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {language === 'es' 
                  ? '¿Tiene preguntas? Estamos aquí para ayudarle.'
                  : 'Have questions? We\'re here to help.'}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <motion.div {...fadeInUp}>
                <h2 className="text-xl font-semibold text-slate-900 mb-6">
                  {language === 'es' ? 'Información de Contacto' : 'Contact Information'}
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">Email</h3>
                      <a 
                        href="mailto:support@pecashglobal.com"
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        support@pecashglobal.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">
                        {language === 'es' ? 'Teléfono' : 'Phone'}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        [PLACEHOLDER: Support phone number]
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">
                        {language === 'es' ? 'Dirección' : 'Address'}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        [PLACEHOLDER: Business address]
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <h3 className="font-medium text-slate-900 mb-4">
                    {language === 'es' ? 'Enlaces Rápidos' : 'Quick Links'}
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <a 
                        href="/faq"
                        className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm"
                      >
                        <HelpCircle className="w-4 h-4" />
                        {language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
                      </a>
                    </li>
                    <li>
                      <a 
                        href="/trust"
                        className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm"
                      >
                        <HelpCircle className="w-4 h-4" />
                        {language === 'es' ? 'Centro de Confianza' : 'Trust Center'}
                      </a>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-6">
                  {language === 'es' ? 'Envíenos un Mensaje' : 'Send Us a Message'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {language === 'es' ? 'Nombre' : 'Name'}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder={language === 'es' ? 'Su nombre' : 'Your name'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder={language === 'es' ? 'su@email.com' : 'your@email.com'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {language === 'es' ? 'Asunto' : 'Subject'}
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {subjects.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {language === 'es' ? 'Mensaje' : 'Message'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                      placeholder={language === 'es' ? '¿Cómo podemos ayudarle?' : 'How can we help you?'}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {language === 'es' ? 'Enviando...' : 'Sending...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {language === 'es' ? 'Enviar Mensaje' : 'Send Message'}
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
